function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import cn from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'components/Link';
import intlShape from 'shapes/intlShape';
import Loader from 'svg/loader.svg';
var _React_PureComponent;
/**
 * Button component
 * @param {node} children - Content with the greatest priority
 * @param {Object} labelMessage - Intl message definition for button label which is used if no children are passed
 * @param {Object} labelMessageValues - values that are replacing variables in an intl message
 * @param {string='default','primary','success','info','warning','danger','link'} [styleModifier=default]
 *        - alternate button class modifier
 * @param {string='button','submit','reset','link'} [type=button] - button type
 * @param {string} [to=''] - if button is an anchor it's possible to add href address
 * @param {boolean} [isInProgress=false] - injected action progress indicator, generally connected
 *        with some store state
 * @param {boolean} [isDisabled=false] - flag to disable button actions
 * @param {(string|Object)} [className] - additional Component className
 * @param {func} [onClick] - injected action triggered by button click
 */ class Button extends (_React_PureComponent = React.PureComponent) {
    onClick(evt, isEnabled) {
        if (!isEnabled) {
            evt.preventDefault();
            if (this.props.isDisabled && this.props.onClickDisabled) {
                this.props.onClickDisabled(evt);
            }
            return;
        }
        this.props.onClick(evt);
    }
    get isInProgress() {
        return this.props.isInProgress;
    }
    renderAnchor() {
        const isEnabled = !this.props.isDisabled && !this.props.isInProgress;
        return /*#__PURE__*/ _jsx(Link, _object_spread_props(_object_spread({
            id: this.props.id,
            to: isEnabled ? this.props.to : '',
            className: cn('btn', {
                [`btn--${this.props.styleModifier}`]: this.props.styleModifier,
                'btn--disabled': this.props.isDisabled,
                'btn--in-progress': this.props.isInProgress
            }, this.props.className)
        }, this.props.data), {
            onClick: (evt)=>this.onClick(evt, isEnabled),
            children: /*#__PURE__*/ _jsx("span", {
                className: "btn-inner",
                children: this.renderLabel()
            })
        }));
    }
    renderLoader() {
        if (!this.isInProgress) {
            return null;
        }
        return /*#__PURE__*/ _jsx(motion.div, {
            initial: {
                x: this.isInitInProgress ? 0 : '100%'
            },
            animate: {
                x: 0
            },
            exit: {
                x: '100%'
            },
            transition: {
                ease: 'easeOut',
                duration: 0.15
            },
            className: "btn__loaderWrapper",
            children: /*#__PURE__*/ _jsx(Loader, {
                className: "btn__loader rotatingLoader"
            })
        });
    }
    renderLabel() {
        const { children, intl, labelMessage, labelMessageValues } = this.props;
        let label = null;
        if (children) {
            label = children;
        }
        if (labelMessage) {
            label = intl.formatMessage(labelMessage, labelMessageValues);
        }
        if (!label) {
            return null;
        }
        return /*#__PURE__*/ _jsx(motion.div, {
            initial: this.isInitInProgress ? {
                x: '-100%'
            } : {
                x: 0
            },
            animate: this.isInProgress ? {
                x: '-100%'
            } : {
                x: 0
            },
            transition: {
                ease: 'easeOut',
                duration: 0.15
            },
            className: "btn__labelWrapper",
            children: label
        });
    }
    renderButton() {
        const isEnabled = (!this.props.isDisabled || !!this.props.onClickDisabled) && !this.props.isInProgress;
        return /* eslint-disable react/button-has-type */ /*#__PURE__*/ _jsx("button", _object_spread_props(_object_spread({
            id: this.props.id,
            type: this.props.type,
            className: cn('btn', {
                [`btn--${this.props.styleModifier}`]: this.props.styleModifier,
                'btn--disabled': this.props.isDisabled,
                'btn--in-progress': this.props.isInProgress
            }, this.props.className)
        }, this.props.data), {
            disabled: !isEnabled,
            onClick: (evt)=>this.onClick(evt, isEnabled),
            children: /*#__PURE__*/ _jsxs("span", {
                className: "btn-inner",
                children: [
                    this.renderLabel(),
                    /*#__PURE__*/ _jsx(AnimatePresence, {
                        children: this.renderLoader()
                    })
                ]
            })
        }));
    }
    render() {
        if (this.props.type === 'link') {
            return this.renderAnchor();
        }
        return this.renderButton();
    }
    constructor(props){
        super(props);
        this.isInitInProgress = props.isInProgress;
    }
}
_define_property(Button, "propTypes", {
    children: PropTypes.node,
    labelMessage: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    labelMessageValues: PropTypes.object,
    styleModifier: PropTypes.string,
    type: PropTypes.oneOf([
        'button',
        'submit',
        'reset',
        'link'
    ]),
    to: PropTypes.string,
    data: PropTypes.object,
    isInProgress: PropTypes.bool,
    isDisabled: PropTypes.bool,
    className: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    onClick: PropTypes.func,
    onClickDisabled: PropTypes.func,
    intl: intlShape.isRequired,
    id: PropTypes.string
});
_define_property(Button, "defaultProps", {
    type: 'button',
    to: '',
    data: {},
    isInProgress: false,
    isDisabled: false,
    onClick: ()=>{}
});
export default injectIntl(Button);
